import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import { InputNumber } from "@/components/InputNumber";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Button, Form, Modal, Tabs } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalcValueTag } from "../../../components/recalculation/CalcValueTag";
import type { RealtyCalc } from "../../types";
import type { RecalculationForm } from "./RealtyCalcRecalculationForm";

const AMOUNT_ROW_PROPS = {
  layout: "horizontal",
  labelAlign: "left",
  labelCol: { flex: "auto" },
  wrapperCol: { flex: "180px" },
  colon: false,
} as const;

type Props = {
  calcData: RealtyCalc;
};

export const RealtyCalcOtherInsuranceAmounts = ({ calcData }: Props) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);

  const form = Form.useFormInstance<RecalculationForm>();
  const realtyAmounts = Form.useWatch("realtyInsuranceData", form);
  const householdAmounts = Form.useWatch("householdInsuranceData", form);

  const nearbyBuildings = calcData.realtyInsuranceData?.nearbyBuildingReinsurances ?? [];
  const equipment = calcData.householdInsuranceData?.equipmentReinsurances ?? [];
  const isHouseholdInsured = !!calcData.householdInsuranceData;
  const { permanentlyOccupied } = calcData.generalBuildingData;

  if (!nearbyBuildings.length && !equipment.length && !isHouseholdInsured) {
    return null;
  }

  const listAmounts = [
    ...(realtyAmounts?.nearbyBuildingReinsurances ?? []),
    ...(householdAmounts?.equipmentReinsurances ?? []),
  ].map((item) => item.insuranceAmount);

  const premiumAmounts = isHouseholdInsured
    ? [
        householdAmounts?.electronicsInsuranceAmount,
        householdAmounts?.artworksInsuranceAmount,
        householdAmounts?.valuablesInsuranceAmount,
      ]
    : [];

  const filledCount = [...listAmounts, ...premiumAmounts].filter(isDefined).length;
  const isRequiredAmountMissing = listAmounts.some((amount) => !isDefined(amount));

  const handleConfirm = () => {
    form
      .validateFields([
        ...nearbyBuildings.map((_, index) => [
          "realtyInsuranceData",
          "nearbyBuildingReinsurances",
          index,
          "insuranceAmount",
        ]),
        ...equipment.map((_, index) => ["householdInsuranceData", "equipmentReinsurances", index, "insuranceAmount"]),
        ["householdInsuranceData", "electronicsInsuranceAmount"],
        ["householdInsuranceData", "artworksInsuranceAmount"],
        ["householdInsuranceData", "valuablesInsuranceAmount"],
      ])
      .then(() => setOpen(false))
      .catch(() => undefined);
  };

  const nearbyBuildingLabel = (index: number) => {
    const building = nearbyBuildings[index];

    return (
      `${index + 1}. ${t(`calc.realty.enums.nearbyBuildingType.${building?.type}`)}` +
      (building?.otherDesc ? `: ${building.otherDesc}` : "")
    );
  };

  const equipmentLabel = (index: number) => {
    const item = equipment[index];
    const product = [item?.manufacturer, item?.model].filter(Boolean).join(" ");

    return (
      `${index + 1}. ${t(`calc.realty.enums.householdEquipmentType.${item?.type}`)}` +
      (item?.otherDesc ? `: ${item.otherDesc}` : "") +
      (product ? ` (${product})` : "")
    );
  };

  return (
    <>
      <CalcValueTag
        label={t("calc.realty.sections.otherInsuranceAmounts")}
        value={filledCount}
        error={isRequiredAmountMissing}
        onClick={() => setOpen(true)}
      />

      <Modal
        title={t("calc.realty.sections.otherInsuranceAmounts")}
        open={open}
        width={640}
        onCancel={() => setOpen(false)}
        footer={
          <Button type="primary" onClick={handleConfirm}>
            {t("common.ok")}
          </Button>
        }
      >
        <Tabs
          items={[
            ...(nearbyBuildings.length
              ? [
                  {
                    key: "nearbyBuildings",
                    label: t("calc.realty.sections.realtyReinsurances"),
                    children: (
                      <Form.List name={["realtyInsuranceData", "nearbyBuildingReinsurances"]}>
                        {(fields) =>
                          fields.map((field) => (
                            <Form.Item
                              key={field.key}
                              {...AMOUNT_ROW_PROPS}
                              name={[field.name, "insuranceAmount"]}
                              label={nearbyBuildingLabel(field.name)}
                              rules={[validations.notNull, validations.minNumber(1), validations.multipleOf100]}
                            >
                              <InputNumber addon="euro" step={100} />
                            </Form.Item>
                          ))
                        }
                      </Form.List>
                    ),
                  },
                ]
              : []),
            ...(equipment.length
              ? [
                  {
                    key: "equipment",
                    label: t("calc.realty.helpers.equipmentReinsurancesShort"),
                    children: (
                      <Form.List name={["householdInsuranceData", "equipmentReinsurances"]}>
                        {(fields) =>
                          fields.map((field) => (
                            <Form.Item
                              key={field.key}
                              {...AMOUNT_ROW_PROPS}
                              name={[field.name, "insuranceAmount"]}
                              label={equipmentLabel(field.name)}
                              rules={[
                                validations.notNull,
                                validations.minNumber(1),
                                validations.maxNumber(50_000),
                                validations.multipleOf100,
                              ]}
                            >
                              <InputNumber addon="euro" step={100} />
                            </Form.Item>
                          ))
                        }
                      </Form.List>
                    ),
                  },
                ]
              : []),
            ...(isHouseholdInsured
              ? [
                  {
                    key: "premium",
                    label: t("calc.realty.helpers.additionalHouseholdAmountsShort"),
                    children: (
                      <>
                        <Form.Item
                          {...AMOUNT_ROW_PROPS}
                          name={["householdInsuranceData", "electronicsInsuranceAmount"]}
                          label={
                            <LabelWithTooltip
                              label={t("calc.realty.attrs.householdInsuranceData.electronicsInsuranceAmount")}
                              tooltip={t("calc.realty.helpers.electronicsInsuranceAmountDesc")}
                            />
                          }
                          rules={[
                            validations.minNumber(1),
                            validations.maxNumber(permanentlyOccupied ? 20_000 : 2_000),
                            validations.multipleOf100,
                          ]}
                        >
                          <InputNumber addon="euro" step={100} />
                        </Form.Item>
                        <Form.Item
                          {...AMOUNT_ROW_PROPS}
                          name={["householdInsuranceData", "artworksInsuranceAmount"]}
                          label={
                            <LabelWithTooltip
                              label={t("calc.realty.attrs.householdInsuranceData.artworksInsuranceAmount")}
                              tooltip={t("calc.realty.helpers.artworksInsuranceAmountDesc")}
                            />
                          }
                          rules={[validations.minNumber(1), validations.maxNumber(10_000), validations.multipleOf100]}
                        >
                          <InputNumber addon="euro" step={100} disabled={!permanentlyOccupied} />
                        </Form.Item>
                        <Form.Item
                          {...AMOUNT_ROW_PROPS}
                          name={["householdInsuranceData", "valuablesInsuranceAmount"]}
                          label={
                            <LabelWithTooltip
                              label={t("calc.realty.attrs.householdInsuranceData.valuablesInsuranceAmount")}
                              tooltip={t("calc.realty.helpers.valuablesInsuranceAmountDesc")}
                            />
                          }
                          rules={[validations.minNumber(1), validations.maxNumber(10_000), validations.multipleOf100]}
                        >
                          <InputNumber addon="euro" step={100} disabled={!permanentlyOccupied} />
                        </Form.Item>
                      </>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Modal>
    </>
  );
};
